import React, { FC, useEffect, useState } from "react";
import axios from "axios";
import { useSearchParams, useNavigate } from "react-router-dom";
import { base_url } from "constant/constant";
import moment from "moment";

import checkmarkIcon from "images/pay/checkmark.svg";
import calendarIcon from "images/pay/calendar.svg";
import guestsIcon from "images/pay/guests.svg";
import bedIcon from "images/card/bed-icon.svg";
import bathIcon from "images/card/bath-icon.svg";
import toiletIcon from "images/card/toilet-icon.svg";
import sleepsIcon from "images/card/sleeps-icon.svg";
import pinIconGold from "images/pay/pin-icon.svg";
import arrowRightIcon from "images/footer/arrow-right.svg";
import ContactFormModal from "shared/Footer/ContactForm";
import useWindowSize from "hooks/useWindowResize";
import { useLocale } from "i18n/locale";
import { formatAmount } from "utils/formatAmount";

export interface PayPageProps {
  className?: string;
}

export interface BookingDetails {
  _id: any;
  createdAt: string;
  city: string;
  city_tax: string;
  cleaning_fees: string;
  number_of_person: number;
  payment_date: string;
  gross_amount: any;
  rent: string;
  start_date: any;
  end_date: any;
  property_id: {
    title: string;
    address: string;
    bedrooms: string;
    bathrooms: string;
    toilets: string;
    image: string;
  };
}

const PayPage: FC<PayPageProps> = () => {
  const { t, locale } = useLocale();
  // The page was inline-styled with a fixed 282px side padding, a fixed 300px
  // property image and non-wrapping rows, so at 100% zoom (≈1280px) the content
  // gets cramped and messy. Mirror the /my-bookings approach: derive breakpoints
  // from window width and switch the problem styles below.
  const { width: winWidth } = useWindowSize();
  const isMobile = winWidth < 768;
  // Below 1024 the property image + details side-by-side run out of room, so we
  // stack them.
  const isNarrow = winWidth < 1024;
  // The footer's side-by-side text + two buttons needs ~680px of content width.
  // Below ~820px (iPad Mini portrait is exactly 768) it no longer fits and the
  // buttons collapse under the text, so the footer stacks (buttons above text)
  // a little earlier than the phone breakpoint.
  const footerStacked = winWidth < 820;
  const sidePad =
    winWidth >= 1280 ? 282 : winWidth >= 1024 ? 120 : winWidth >= 768 ? 60 : winWidth >= 480 ? 24 : 16;

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [paymentStatus, setPaymentStatus] = useState<string | null>(null);
  const [bookingData, setBookingData] = useState<BookingDetails>();
  const [loading, setLoading] = useState(true);
  const [contactOpen, setContactOpen] = useState(false);
  const [formValues, setFormValues] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
    isChecked: false,
  });

  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    if (sessionId) {
      axios
        .get(`${base_url}/stripe/payment-status?session_id=${sessionId}`)
        .then((response) => {
          setBookingData(response.data.bookingData);
          setPaymentStatus(response.data.session.payment_method_types[0]);
          setLoading(false);
        })
        .catch(() => {
          navigate("/page404", { replace: true });
        });
    } else {
      navigate("/", { replace: true });
    }
  }, [searchParams, navigate]);

  if (loading) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "#fff",
          fontFamily: "Poppins, sans-serif",
          fontSize: 18,
          color: "#01152E",
        }}
      >
        {t("common.loading")}
      </div>
    );
  }

  if (!bookingData) {
    navigate("/page404", { replace: true });
    return null;
  }

  const dateRange = `${moment(bookingData.start_date).format("DD MMM")} - ${moment(bookingData.end_date).format("DD MMM")}, ${moment(bookingData.end_date).format("YYYY")}`;
  // Locale-aware, like the listing cards and the price breakdown: French uses a
  // comma decimal and space grouping ("1 274,00"), English a dot and no
  // grouping ("1274.00"). Plain toFixed(2) always produced the English form.
  const total = formatAmount(
    Number(bookingData.gross_amount) + Number(bookingData.city_tax),
    locale
  );

  return (
    <div style={{ backgroundColor: "#fff", minHeight: "100vh" }}>
      {/* Logo header */}
      <div style={{ padding: `22px ${sidePad}px` }}>
        <a href="/">
          <img
            src="/logo/europea-residences-group.svg"
            alt="Europea"
            style={{ width: 129, height: 32 }}
          />
        </a>
      </div>

      {/* Content area */}
      <div style={{ padding: `0 ${sidePad}px` }}>
        {/* Teal confirmation banner */}
        <div
          style={{
            backgroundColor: "#01152E",
            padding: isMobile ? 24 : 40,
            display: "flex",
            alignItems: "flex-start",
            gap: isMobile ? 14 : 20,
            borderRadius: 0,
          }}
        >
          <img
            src={checkmarkIcon}
            alt=""
            style={{ width: 56, height: 56, flexShrink: 0 }}
          />
          <div>
            <p
              style={{
                fontFamily: "Poppins, sans-serif",
                fontWeight: 500,
                fontSize: 14,
                lineHeight: "16px",
                color: "#B88333",
                textTransform: "uppercase",
                letterSpacing: "1.4px",
                margin: "0 0 4px 0",
              }}
            >
              {t("payDone.confirmed")}
            </p>
            <p
              style={{
                fontFamily: "Poppins, sans-serif",
                fontWeight: 600,
                fontSize: 24,
                lineHeight: "28px",
                color: "#fff",
                margin: "0 0 4px 0",
              }}
            >
              {t("payDone.congrats")}
            </p>
            <p
              style={{
                fontFamily: "Roboto, sans-serif",
                fontWeight: 400,
                fontSize: 14,
                lineHeight: "16px",
                color: "#D6D6D6",
                margin: 0,
              }}
            >
              {t("payDone.confirmationSent")}
            </p>
          </div>
        </div>

        {/* Property row */}
        <div style={{ display: "flex", flexDirection: isNarrow ? "column" : "row", gap: isNarrow ? 20 : 30, marginTop: 30 }}>
          {/* Property image */}
          <img
            src={bookingData?.property_id?.image}
            alt={bookingData?.property_id?.title}
            style={{
              width: isNarrow ? "100%" : 300,
              height: 200,
              borderRadius: 10,
              objectFit: "cover",
              flexShrink: 0,
            }}
          />

          {/* Property details */}
          <div style={{ flex: 1, minWidth: 0 }}>
            {/* Title */}
            <h2
              style={{
                fontFamily: "Poppins, sans-serif",
                fontWeight: 600,
                fontSize: 24,
                lineHeight: "28px",
                letterSpacing: "0.48px",
                color: "#01152E",
                margin: 0,
              }}
            >
              {bookingData?.property_id?.title}
            </h2>

            {/* Location */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                marginBottom: 12,
              }}
            >
              <img src={pinIconGold} alt="" style={{ width: 9, height: 11 }} />
              <span
                style={{
                  fontFamily: "Poppins, sans-serif",
                  fontWeight: 400,
                  fontSize: 14,
                  lineHeight: "24px",
                  color: "#445162",
                }}
              >
                {bookingData?.property_id?.address}
              </span>
            </div>

            {/* Amenities row */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                flexWrap: "wrap",
                rowGap: 6,
                gap: 12,
                fontFamily: "Poppins, sans-serif",
                fontWeight: 400,
                fontSize: 14,
                lineHeight: "24px",
                color: "#445162",
                marginBottom: 24,
              }}
            >
              <div
                style={{ display: "flex", alignItems: "center", gap: 6 }}
              >
                <img src={bedIcon} alt="" style={{ width: 19, height: 14 }} />
                <span>
                  {bookingData?.property_id?.bedrooms ?? 1} {t("payDone.bedrooms")}
                </span>
              </div>
              <span style={{ color: "#D6D6D6" }}>|</span>
              <div
                style={{ display: "flex", alignItems: "center", gap: 6 }}
              >
                <img
                  src={bathIcon}
                  alt=""
                  style={{ width: 14, height: 14 }}
                />
                <span>
                  {bookingData?.property_id?.bathrooms ?? 1} {t("payDone.bathrooms")}
                </span>
              </div>
              <span style={{ color: "#D6D6D6" }}>|</span>
              <div
                style={{ display: "flex", alignItems: "center", gap: 6 }}
              >
                <img
                  src={toiletIcon}
                  alt=""
                  style={{ width: 12, height: 14 }}
                />
                <span>
                  {bookingData?.property_id?.toilets ?? 1} {t("payDone.toilets")}
                </span>
              </div>
              <span style={{ color: "#D6D6D6" }}>|</span>
              <div
                style={{ display: "flex", alignItems: "center", gap: 6 }}
              >
                <img
                  src={sleepsIcon}
                  alt=""
                  style={{ width: 18, height: 14 }}
                />
                <span>{t("payDone.sleeps")} {bookingData?.number_of_person}</span>
              </div>
            </div>

            {/* Date/Guests box */}
            <div
              style={{
                border: "1px solid #D6D6D6",
                borderRadius: 8,
                display: "flex",
                minHeight: 72,
              }}
            >
              {/* Date section */}
              <div style={{ flex: 1, minWidth: 0, padding: isMobile ? "12px 14px" : 20, display: "flex", alignItems: "center", gap: isMobile ? 12 : 16 }}>
                <img
                  src={calendarIcon}
                  alt=""
                  style={{ width: 26, height: 27, flexShrink: 0 }}
                />
                <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
                  <span
                    style={{
                      fontFamily: "Poppins, sans-serif",
                      fontWeight: 500,
                      fontSize: 12,
                      lineHeight: "24px",
                      color: "#445162",
                      textTransform: "uppercase",
                      letterSpacing: "1.2px",
                    }}
                  >
                    {t("payDone.dateLabel")}
                  </span>
                  <p
                    style={{
                      fontFamily: "Poppins, sans-serif",
                      fontWeight: 600,
                      fontSize: 16,
                      lineHeight: "24px",
                      color: "#01152E",
                      margin: 0,
                    }}
                  >
                    {dateRange}
                  </p>
                </div>
              </div>

              {/* Vertical divider */}
              <div style={{ width: 1, backgroundColor: "#D6D6D6", flexShrink: 0 }} />

              {/* Guests section */}
              <div style={{ flex: 1, minWidth: 0, padding: isMobile ? "12px 14px" : 20, display: "flex", alignItems: "center", gap: isMobile ? 12 : 16 }}>
                <img
                  src={guestsIcon}
                  alt=""
                  style={{ width: 32, height: 29, flexShrink: 0 }}
                />
                <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
                  <span
                    style={{
                      fontFamily: "Poppins, sans-serif",
                      fontWeight: 500,
                      fontSize: 12,
                      lineHeight: "24px",
                      color: "#445162",
                      textTransform: "uppercase",
                      letterSpacing: "1.2px",
                    }}
                  >
                    {t("payDone.guestsLabel")}
                  </span>
                  <p
                    style={{
                      fontFamily: "Poppins, sans-serif",
                      fontWeight: 600,
                      fontSize: 16,
                      lineHeight: "24px",
                      color: "#01152E",
                      margin: 0,
                    }}
                  >
                    {bookingData?.number_of_person} {t("common.guests")}
                  </p>
                </div>
              </div>
            </div>

            {/* Booking Details */}
            <h3
              style={{
                fontFamily: "Poppins, sans-serif",
                fontWeight: 600,
                fontSize: 18,
                lineHeight: "24px",
                letterSpacing: "0.36px",
                color: "#01152E",
                margin: "30px 0 16px 0",
              }}
            >
              {t("payDone.bookingDetails")}
            </h3>

            {/* Detail rows */}
            <div>
              <div style={{ height: 1, backgroundColor: "#D6D6D6" }} />
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  padding: "14px 0",
                }}
              >
                <span
                  style={{
                    fontFamily: "Poppins, sans-serif",
                    fontWeight: 500,
                    fontSize: 16,
                    lineHeight: "24px",
                    color: "#01152E",
                  }}
                >
                  {t("payDone.dateRow")}
                </span>
                <span
                  style={{
                    fontFamily: "Poppins, sans-serif",
                    fontWeight: 500,
                    fontSize: 16,
                    lineHeight: "24px",
                    color: "#445162",
                  }}
                >
                  {moment(bookingData?.createdAt).format("DD MMM, YYYY")}
                </span>
              </div>
              <div style={{ height: 1, backgroundColor: "#D6D6D6" }} />
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  padding: "14px 0",
                }}
              >
                <span
                  style={{
                    fontFamily: "Poppins, sans-serif",
                    fontWeight: 500,
                    fontSize: 16,
                    lineHeight: "24px",
                    color: "#01152E",
                  }}
                >
                  {t("payDone.total")}
                </span>
                <span
                  style={{
                    fontFamily: "Poppins, sans-serif",
                    fontWeight: 500,
                    fontSize: 16,
                    lineHeight: "24px",
                    color: "#445162",
                  }}
                >
                  &euro; {total}
                </span>
              </div>
              <div style={{ height: 1, backgroundColor: "#D6D6D6" }} />
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  padding: "14px 0",
                }}
              >
                <span
                  style={{
                    fontFamily: "Poppins, sans-serif",
                    fontWeight: 500,
                    fontSize: 16,
                    lineHeight: "24px",
                    color: "#01152E",
                  }}
                >
                  {t("payDone.paymentMethod")}
                </span>
                <span
                  style={{
                    fontFamily: "Poppins, sans-serif",
                    fontWeight: 500,
                    fontSize: 16,
                    lineHeight: "24px",
                    color: "#445162",
                    textTransform: "capitalize",
                  }}
                >
                  {paymentStatus}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Full-width separator */}
        <div
          style={{ height: 1, backgroundColor: "#D6D6D6" }}
        />

        {/* Footer row */}
        <div
          style={{
            display: "flex",
            // column-reverse when stacked so the action buttons sit above the
            // "Questions about your stay?" line rather than below it.
            flexDirection: footerStacked ? "column-reverse" : "row",
            justifyContent: "space-between",
            alignItems: footerStacked ? "stretch" : "center",
            gap: footerStacked ? 16 : 20,
            marginTop: 24,
            paddingBottom: 40,
          }}
        >
          <p
            style={{
              fontFamily: "Poppins, sans-serif",
              fontWeight: 400,
              fontSize: 14,
              lineHeight: "18px",
              color: "#445162",
              margin: 0,
            }}
          >
            {t("payDone.questions")}{" "}
            <span
              onClick={() => setContactOpen(true)}
              style={{
                fontWeight: 600,
                textDecoration: "underline",
                color: "#01152E",
                cursor: "pointer",
              }}
            >
              {t("payDone.contactUs")}
            </span>
          </p>

          <div style={{ display: "flex", flexWrap: "wrap", gap: 16, width: footerStacked ? "100%" : "auto" }}>
            <a
              href="/"
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                flex: footerStacked ? 1 : "none",
                // Sized from its own label rather than a fixed 180x44 box. The
                // French label ("Decouvrir d'autres sejours") is much longer
                // than the English one and used to wrap straight out of the
                // pill; padding plus a minHeight let the pill grow to fit.
                minWidth: footerStacked ? 140 : undefined,
                padding: footerStacked ? "10px 10px" : "10px 22px",
                minHeight: 44,
                textAlign: "center",
                border: "1px solid #01152E",
                borderRadius: 22,
                fontFamily: "Poppins, sans-serif",
                fontSize: 14,
                fontWeight: 500,
                lineHeight: "20px",
                color: "#01152E",
                textDecoration: "none",
                backgroundColor: "transparent",
              }}
            >
              {t("payDone.exploreMore")}
            </a>
            <a
              href={`/my-bookings?id=${bookingData?._id}`}
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
                flex: footerStacked ? 1 : "none",
                // Same reasoning as the outlined button above: no fixed width,
                // so "Voir la reservation" cannot overflow the pill.
                minWidth: footerStacked ? 140 : undefined,
                padding: footerStacked ? "10px 10px" : "10px 22px",
                minHeight: 44,
                textAlign: "center",
                backgroundColor: "#01152E",
                borderRadius: 22,
                fontFamily: "Poppins, sans-serif",
                fontSize: 14,
                fontWeight: 500,
                lineHeight: "20px",
                color: "#fff",
                textDecoration: "none",
                border: "none",
              }}
            >
              {t("payDone.viewBooking")}
              <img
                src={arrowRightIcon}
                alt=""
                style={{ width: 13, height: 12 }}
              />
            </a>
          </div>
        </div>
      </div>
      <ContactFormModal
        isOpen={contactOpen}
        onClose={() => {
          setContactOpen(false);
          setFormValues({ name: "", email: "", phone: "", message: "", isChecked: false });
        }}
        formValues={formValues}
        setFormValues={setFormValues}
      />
    </div>
  );
};

export default PayPage;
